import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { taskApi, checkinApi, userApi } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  MapPin,
  User,
  CalendarDays,
  Clock,
  Phone,
  FileText,
  CheckCircle,
  XCircle,
  Navigation
} from 'lucide-react';

export default function TaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [nearbyTasks, setNearbyTasks] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [taskRes, checkinsRes, nearbyRes] = await Promise.all([
        taskApi.getById(id),
        checkinApi.getAll({ task_id: id }),
        taskApi.getNearby(id, 5),
      ]);
      setTask(taskRes.data);
      setCheckins(checkinsRes.data);
      setNearbyTasks(nearbyRes.data);

      if (user?.role !== 'salesperson') {
        const salespersonsRes = await userApi.getSalespersons();
        setSalespersons(salespersonsRes.data);
      }
    } catch (err) {
      toast.error('Failed to load task details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await taskApi.update(id, { status: newStatus });
      setTask({ ...task, status: newStatus });
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignChange = async (userId) => {
    try {
      await taskApi.update(id, { assigned_to: userId });
      fetchData();
      toast.success('Task assigned');
    } catch (err) {
      toast.error('Failed to assign task');
    }
  };

  const handleCheckIn = async (type) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await checkinApi.create({
            task_id: id,
            check_type: type,
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          });
          toast.success(`${type === 'checkin' ? 'Checked in' : 'Checked out'} successfully`);
          fetchData();
        } catch (err) {
          toast.error(`Failed to ${type}`);
        }
      },
      (err) => {
        toast.error('Could not get location');
      }
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </MainLayout>
    );
  }

  if (!task) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Task not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="task-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>{task.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={getPriorityColor(task.priority)}>{task.priority} priority</Badge>
              <Badge variant="outline" className={getStatusColor(task.status)}>
                {task.status.replace('_', ' ')}
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>Task Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {task.description && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Description</p>
                    <p className="text-gray-900">{task.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Customer</p>
                      <p className="font-medium text-gray-900">{task.customer_name}</p>
                    </div>
                  </div>
                  {task.customer_phone && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Phone</p>
                        <p className="font-medium text-gray-900">{task.customer_phone}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="font-medium text-gray-900">{task.customer_location?.address || 'No address'}</p>
                    </div>
                  </div>
                  {task.due_date && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <CalendarDays className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Due Date</p>
                        <p className="font-medium text-gray-900">{task.due_date}</p>
                      </div>
                    </div>
                  )}
                </div>

                {task.notes && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Notes</p>
                      <p className="text-gray-900">{task.notes}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Check-in History */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                {checkins.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No activity yet</p>
                ) : (
                  <div className="space-y-3">
                    {checkins.map((checkin) => (
                      <div key={checkin.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        {checkin.check_type === 'checkin' ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {checkin.check_type === 'checkin' ? 'Checked In' : 'Checked Out'}
                          </p>
                          <p className="text-sm text-gray-500">
                            by {checkin.user_name} • {new Date(checkin.timestamp).toLocaleString()}
                          </p>
                          {checkin.notes && (
                            <p className="text-sm text-gray-600 mt-1">{checkin.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {user?.role !== 'salesperson' && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Update Status</p>
                      <Select value={task.status} onValueChange={handleStatusChange}>
                        <SelectTrigger data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Assign To</p>
                      <Select value={task.assigned_to || ''} onValueChange={handleAssignChange}>
                        <SelectTrigger data-testid="assign-select">
                          <SelectValue placeholder="Select person" />
                        </SelectTrigger>
                        <SelectContent>
                          {salespersons.map((sp) => (
                            <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {user?.role === 'salesperson' && task.status !== 'completed' && (
                  <div className="space-y-2">
                    {task.status === 'pending' && (
                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 text-white checkin-btn"
                        onClick={() => handleCheckIn('checkin')}
                        data-testid="checkin-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Check In
                      </Button>
                    )}
                    {task.status === 'in_progress' && (
                      <Button
                        className="w-full bg-red-600 hover:bg-red-700 text-white checkin-btn"
                        onClick={() => handleCheckIn('checkout')}
                        data-testid="checkout-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Check Out
                      </Button>
                    )}
                  </div>
                )}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const loc = task.customer_location;
                    if (loc?.lat && loc?.lng) {
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`, '_blank');
                    }
                  }}
                  data-testid="navigate-btn"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Navigate
                </Button>
              </CardContent>
            </Card>

            {/* Nearby Tasks */}
            {nearbyTasks.length > 0 && (
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Nearby Tasks</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {nearbyTasks.slice(0, 5).map((nt) => (
                      <div
                        key={nt.id}
                        className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => navigate(`/tasks/${nt.id}`)}
                        data-testid={`nearby-task-${nt.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900 text-sm truncate">{nt.customer_name}</p>
                          <Badge className={getPriorityColor(nt.priority)} variant="outline">
                            {nt.priority}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {nt.distance_km} km away
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
